import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Device } from '@twilio/voice-sdk';
import { authFetch } from '../services/authFetch';
import { useToast } from '../context/ToastContext';
import CountrySelect from './CountrySelect';
import countries from '../data/countries.json'
import { useNavigate, Link } from 'react-router-dom';
import { emergencyNumbers } from '../data/emergencyNumbers';
import FeedbackModal from './FeedbackModal';
import { Globe, Plus, Phone, PhoneOff, Mic, MicOff, Trash2, Wallet } from 'lucide-react';
import { riskyPrefixes } from '../data/riskyPrefixes';
import { io } from 'socket.io-client';

const WebPhone = () => {
  const { token, login, logout, loading } = useAuth();
  const [user, setUser] = useState();
  const [device, setDevice] = useState(null);
  const [localNumber, setLocalNumber] = useState('');
  const [status, setStatus] = useState('Call Disconnected');
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [lowCreditWarning, setLowCreditWarning] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null);
  const [showDialpad, setShowDialpad] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState({
    name: "United States", dialCode: "+1", isoCode: "US", emoji: "🇺🇸"
  });

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [verifiedNumbers, setVerifiedNumbers] = useState([]);
  const [selectedCallerId, setSelectedCallerId] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showCallerDropdown, setShowCallerDropdown] = useState(false);
  const [importNumber, setImportNumber] = useState('');
  const [validationCode, setValidationCode] = useState(null);
  const [callerIdError, setCallerIdError] = useState('');
  const dropdownRef = useRef();
  const [isRiskyNumber, setIsRiskyNumber] = useState(false);
  const [liveRate, setLiveRate] = useState(null);
  const socketRef = useRef(null);

  const [showFeedback, setShowFeedback] = useState(false);

  const { addToast } = useToast();
  const navigate = useNavigate();


  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const deviceRef = useRef(null);

  const fetchToken = async () => {
    const res = await authFetch(
      `${import.meta.env.VITE_BASE_URL}/api/token`,
      {},
      { token, login, logout }
    );

    if (!res) {
      return;
    }

    if (!res.ok) {
      if (res.status === 403) {
        addToast('warning', 'Please verify your email before making calls.');
      } else if (res.status === 402) {
        addToast('warning', 'You do not have enough credits to make this call.');
      } else {
        addToast('error', 'Something went wrong. Please try again.');
      }

      return;
    }

    const data = await res.json();
    return data.token;
  };

  const fetchCredits = async () => {
    const res = await authFetch(
      `${import.meta.env.VITE_BASE_URL}/api/protected/me`,
      {},
      { token, login, logout }
    );

    if (!res || !res.ok) {
      addToast('error', "Error when fetching credits. Try again now");
      return null;
    }

    const data = await res.json();
    setRemainingCredits(data.user.credits);
    setUser(data.user);
    return data.user.credits;
  };


  const fetchContacts = async () => {
    try {
      const res = await authFetch(`${import.meta.env.VITE_BASE_URL}/api/contacts`, {}, { token, login, logout });
      if (res.ok) {
        const json = await res.json();
        setContacts(json);
      }
    } catch (err) {
      console.error("Failed to load contacts", err);
    }
  };

  const saveContact = async (contact) => {
    try {
      const res = await authFetch(
        `${import.meta.env.VITE_BASE_URL}/api/contacts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(contact)
        },
        { token, login, logout }
      );
  
      if (res && res.ok) {
        addToast('success', 'Contact saved!');
        await fetchContacts();
      } else {
        addToast('error', 'Could not save contact.');
      }
    } catch (err) {
      console.error('Error saving contact:', err);
      addToast('error', 'Something went wrong.');
    }
  };

  const deleteContact = async (id) => {
    try {
      const res = await authFetch(
        `${import.meta.env.VITE_BASE_URL}/api/contacts/${id}`,
        { method: 'DELETE' },
        { token, login, logout }
      );
  
      if (res && res.status === 204) {
        addToast('success', 'Contact deleted');
        fetchContacts(); // Refresh the list
      } else {
        addToast('error', 'Failed to delete contact');
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      addToast('error', 'Something went wrong.');
    }
  };

  const fetchVerifiedNumbers = async () => {
    try {
      const res = await authFetch(`${import.meta.env.VITE_BASE_URL}/api/caller-id/verified`, {}, { token, login, logout });
      if (res.ok) {
        const json = await res.json();
        setVerifiedNumbers(json.numbers);
      }
    } catch (err) {
      console.error('Failed to fetch verified caller IDs', err);
    }
  };

  function isEmergencyNumber(dialedNumber, countryCode) {
    const cleanNumber = dialedNumber.replace(/\D/g, ''); // remove non-numeric chars
    const countryEmergencyNumbers = emergencyNumbers[countryCode.toUpperCase()] || [];
    return countryEmergencyNumbers.includes(cleanNumber);
  }

  function isHighRiskNumber(phoneNumber) {
    return riskyPrefixes.some(prefix => phoneNumber.startsWith(prefix));
  }

  function isAllowedNumber(numberToCall) {
    const matchedCountry = countries.find(country =>
      numberToCall.startsWith(country.dialCode)
    );
    return Boolean(matchedCountry);
  }

  const checkVerificationStatus = async (phoneNumber) => {
    try {
      const res = await authFetch(
        `${import.meta.env.VITE_BASE_URL}/api/caller-id/verified`,
        {},
        { token, login, logout }
      );
  
      if (res.ok) {
        const json = await res.json();
        setVerifiedNumbers(json.numbers);
        const isNowVerified = json.numbers.some(n => n.phone_number === phoneNumber);
        return isNowVerified;
      }
    } catch (err) {
      console.error('Verification check failed', err);
    }
  
    return false;
  };

  const setupDevice = async () => {
    const tToken = await fetchToken();
    if (!tToken) return;

    const twilioDevice = new Device(tToken, {
      codecPreferences: ['opus', 'pcmu'],
      debug: true,
    });
    
    twilioDevice.register();
    deviceRef.current = twilioDevice;
    setDevice(twilioDevice);
  };

  useEffect(() => {
    if (loading || !token) return;
    setupDevice();
    fetchContacts();
    fetchCredits();
    fetchVerifiedNumbers();
    return () => deviceRef.current?.destroy();
  }, [token, loading]);

  useEffect(() => {
    if (!token || !user?.id) return;

    const socket = io(import.meta.env.VITE_BASE_URL, {
      withCredentials: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {

    // Now fetch the user and register socket
    authFetch(
      `${import.meta.env.VITE_BASE_URL}/api/protected/me`,
      {},
      { token, login, logout }
    )
      .then(res => res.json())
      .then(data => {
        if (data?.user.id) {
          socket.emit('register', data.user.id);
        }
      });
  });

    socket.on('call_error', (data) => {
      if (data.message) {
        addToast('error', data.message);
        setStatus('Call Disconnected');
      }
    });

    return () => {
      socket.disconnect();
    };
}, [token, user]);


  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCallerDropdown(false);
      }
    }
  
    if (showCallerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCallerDropdown]);

  const fetchLiveRate = async () => {
    const digits = localNumber.replace(/[^\d+]/g, '');
    if (!digits || digits.length < 5) {
      setLiveRate(null); // Reset if too short
      return;
    }

    const numberToCheck = digits.startsWith('+')
      ? digits
      : `${selectedCountry.dialCode}${digits.replace(/^0+/, '')}`;

    try {
      const res = await fetch(`${import.meta.env.VITE_BASE_URL}/api/rates/number/${numberToCheck}`);
      const data = await res.json();

      if (res.ok && data.ourRate) {
        setLiveRate(data.ourRate);
        return data.ourRate;
      } else {
        setLiveRate(null);
        return 0.05;
      }
    } catch (err) {
      console.error('Failed to fetch number-specific rate:', err);
      setLiveRate(null);
      return 0.05;
    }
  };

  const makeCall = async (numberToCall = localNumber) => {
    if (!token) {
      addToast('success', 'Sign up to start calling');
      navigate('/register');
      return;
    }

    if (isEmergencyNumber(localNumber, selectedCountry.isoCode)) {
      addToast('error', 'DialVia does not support calling emergency services');
      return;
    }

    if (numberToCall !== 'test') {
      if (localNumber.startsWith('+')) {
        numberToCall = localNumber;
      } else {
        numberToCall = `${selectedCountry.dialCode}${localNumber.replace(/^0+/, '')}`;
      }
    }

    if (isHighRiskNumber(numberToCall)) {
      addToast('warning', '⚠️ Caution: This number may be blocked or fail to connect.');
    }

    let rate = 0.02;
    
    if (numberToCall !== 'test') {
      const credits = await fetchCredits();
      if (!credits) return;
      rate = await fetchLiveRate();
      if (credits - rate < 0) {
        addToast('warning', "You don't have enough credits to make this call");
        return;
      }
    }

    if (!device) {
      addToast('error', 'Make sure your email is verified and you got enough credits');
    }

    if (!device || !numberToCall) return;

    if (!isAllowedNumber(numberToCall)) {
      addToast('error', 'This destination is currently not supported.');
      return;
    }
  
    const connection = await device.connect({ params: { To: numberToCall, ...(selectedCallerId && { CallerId: selectedCallerId.phone_number }) } });
    setActiveConnection(connection);
  
    connection.on('accept', async () => {
      setStatus('In Call');

      if (numberToCall !== 'test') {
        const interval = setInterval(() => {
          setRemainingCredits(prev => {
            if (prev === null) return null;
    
            if ((prev - rate) <= 0) {
              setLowCreditWarning(false);
              deviceRef.current?.disconnectAll();
              clearInterval(interval);
              return 0;
            }
    
            if (prev <= 2 && !lowCreditWarning) {
              setLowCreditWarning(true);
            }
    
            return prev - rate;
          });
        }, 60000);
    
        setCountdownInterval(interval);
      }
    });

    connection.on('ringing', () => {
      setStatus('Ringing');
    });
  
    connection.on('disconnect', () => {
      setStatus('Call Disconnected');
      clearInterval(countdownInterval);
      setIsMuted(false);
      setActiveConnection(null);
      setLowCreditWarning(false);
      setLiveRate(null);
      if (user.hide && !user.has_submitted_feedback && Math.random() < 0.3) {
        setShowFeedback(true);
      };
    });
  };

  const hangUp = () => {
    deviceRef.current?.disconnectAll();
  };

  const toggleMute = () => {
    if (!activeConnection) return;
    const newMutedState = !isMuted;
    activeConnection.mute(newMutedState);
    setIsMuted(newMutedState);
  };
  
  const appendlocalNumber = (digit) => {
    setLocalNumber(prev => prev + digit);
  };
  
  const deleteLastDigit = () => {
    setLocalNumber(prev => prev.slice(0, -1));
  };

  const sendDigitTone = (digit) => {
    if (activeConnection) {
      activeConnection.sendDigits(digit);
    }
  };

  const setCountryDial = (country) => {
    setSelectedCountry(country);
    setIsRiskyNumber(isHighRiskNumber(country.dialCode));
  }
  
  
  return (
    <div className="bg-white rounded-3xl shadow-[0_0_25px_rgba(168,85,247,0.4)] overflow-hidden max-w-md mx-auto p-6 text-gray-900">
      <div className="flex flex-col h-full">
        {/* Header section */}
        <div className="mb-4">
        <div className="flex justify-between items-center w-full">
          {(remainingCredits !== null) && (
            <div className="bg-purple-600 text-white text-sm px-3 py-1 rounded-full backdrop-blur-sm">
              Balance: ${Number(remainingCredits).toFixed(2)}
            </div>
          )}
          <Link
            to="/buy-credits"
            className="flex items-center gap-1 text-sm text-white bg-purple-600 hover:bg-purple-700 px-3 py-1.5 rounded-full transition"
          >
            <Wallet className="w-4 h-4" />
            Buy Credits
          </Link>

        </div>

        <div className="flex justify-between items-center mt-2 flex-wrap gap-y-1">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full mr-2 ${status === 'In Call' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
            <p className="text-gray-700 text-sm">{status}</p>
          </div>
          {token && localNumber && status !== 'In Call' && !selectedContact && !contacts.some(c => c.phone_number === (localNumber.startsWith('+') ? localNumber : c.dial_code + localNumber)) && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="text-sm text-purple-400 underline hover:text-purple-500"
            >
              + Save this number
            </button>
          )}
        </div>
        </div>

        {lowCreditWarning && (
          <p className="text-red-500 text-sm font-semibold mt-1">
            ⚠️ Low credits! Call will end soon.
          </p>
        )}

      </div>

        {/* Phone input */}
        <div className="relative mb-3">
          <div className="flex items-center bg-gray-100 rounded-lg px-3 py-2 space-x-2">
            <CountrySelect selected={selectedCountry} onSelect={setCountryDial} />

            <input
              className="bg-gray-100 text-gray-900 outline-none flex-1 min-w-0"
              placeholder="Enter phone number"
              value={localNumber}
              onChange={(e) => {
                const raw = e.target.value;
                let clean = raw.replace(/[^\d+]/g, '');
              
                // Auto-detect country if number starts with '+'
                if (clean.startsWith('+')) {
                  const match = countries.find(c => clean.startsWith(c.dialCode));
                  if (match) {
                    setSelectedCountry(match);
                  }
                }
              
                // Store full input (cleaned, but keep '+' if present)
                setLocalNumber(clean);
              
                if (selectedContact) {
                  setSelectedContact(null);
                }

                setIsRiskyNumber(isHighRiskNumber(
                  clean.startsWith('+') ? clean : `${selectedCountry.dialCode}${clean.replace(/^0+/, '')}`
                ));
              }}
            />

            {localNumber && (
              <button 
                onClick={deleteLastDigit}
                className="text-gray-700 hover:text-gray-700 shrink-0"
              >
                ⌫
              </button>
            )}
          </div>
        </div>

        {liveRate !== null && (
          <p className="text-sm text-purple-400 mt-1 mb-1 text-center">
            ${liveRate.toFixed(3)}/min
          </p>
        )}


        {/* Caller ID section */}
        <div className="flex items-center mb-3 w-full gap-3">
          <p className="text-gray-700 text-sm whitespace-nowrap mr-2">Call from:</p>

          {/* Flex container for selector and contact button */}
          <div className="flex items-center gap-3 flex-grow">
            {/* Public Number Selector */}
            <div ref={dropdownRef} className="relative inline-block">
              <button
                onClick={() => setShowCallerDropdown(prev => !prev)}
                className="bg-gray-100 border border-gray-300 rounded-full px-3 py-1 flex items-center gap-2 focus:outline-none w-full max-w-xs"
              >
                <Phone className="text-gray-700 w-4 h-4" />
                <span className="text-gray-700 text-sm truncate">
                  {selectedCallerId ? selectedCallerId.phone_number : 'Public number'}
                </span>
                <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.25 7.5L10 12.25L14.75 7.5H5.25Z" />
                </svg>
              </button>

              {showCallerDropdown && (
                <div className="absolute left-0 mt-2 w-60 rounded-md shadow-lg bg-gray-100 ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-2 max-h-64 overflow-y-auto">
                    <button
                      className={`flex items-center gap-3 w-full text-left px-4 py-2 text-sm hover:bg-gray-200 ${
                        !selectedCallerId ? 'text-purple-400' : 'text-gray-700'
                      }`}
                      onClick={() => {
                        setSelectedCallerId(null);
                        setShowCallerDropdown(false);
                      }}
                    >
                      <Globe className="w-5 h-5" />
                      Public number
                    </button>

                    <hr className="border-gray-300 my-1" />

                    <button
                      className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-200"
                      onClick={() => {
                        if (!token) {
                          addToast('success', 'Register to start calling');
                          navigate('/register');
                          return;
                        }
                        setShowVerifyModal(true);
                        setShowCallerDropdown(false);
                      }}
                    >
                      <Plus className="w-5 h-5" />
                      Add Caller ID
                    </button>

                    <hr className="border-gray-300 my-1" />

                    {verifiedNumbers.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedCallerId(item);
                          setShowCallerDropdown(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-200 ${
                          selectedCallerId?.phone_number === item.phone_number
                            ? 'text-purple-400'
                            : 'text-gray-700'
                        }`}
                      >
                        {item.phone_number}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Contacts Button */}
            <button
              onClick={() => setShowContactsModal(true)}
              className="w-9 h-9 flex-shrink-0 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition duration-200 focus:outline-none"
            >
              <svg
                className="w-5 h-5 text-gray-700 hover:text-gray-900 transition duration-200"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>


        {isRiskyNumber && (
          <div className="bg-yellow-500/20 border border-yellow-400 text-yellow-700 text-center text-sm rounded-lg px-4 py-2 mb-4">
            ⚠️ Warning: This call may be blocked or fail to connect due to security reasons.
          </div>
        )}


        {/* Dialpad */}
        {showDialpad && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((digit) => (
              <button
                key={digit}
                onClick={() => {
                  appendlocalNumber(String(digit));
                  if (status === 'In Call') {
                    sendDigitTone(String(digit));
                  }
                 }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-full w-20 h-20 flex flex-col items-center justify-center mx-auto"
              >
                <span className="text-xl">{digit}</span>
                {digit === 1 ? null : 
                  digit === 2 ? <span className="text-xs text-gray-700">ABC</span> :
                  digit === 3 ? <span className="text-xs text-gray-700">DEF</span> :
                  digit === 4 ? <span className="text-xs text-gray-700">GHI</span> :
                  digit === 5 ? <span className="text-xs text-gray-700">JKL</span> :
                  digit === 6 ? <span className="text-xs text-gray-700">MNO</span> :
                  digit === 7 ? <span className="text-xs text-gray-700">PQRS</span> :
                  digit === 8 ? <span className="text-xs text-gray-700">TUV</span> :
                  digit === 9 ? <span className="text-xs text-gray-700">WXYZ</span> :
                  digit === 0 ? <span className="text-xs text-gray-700">+</span> : null
                }
              </button>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={() => setShowDialpad(!showDialpad)}
            className="bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 rounded-full py-3 px-4 flex items-center justify-center"
          >
            123
          </button>
          
          {status !== 'In Call' && status !== 'Ringing' ? (
            <button
              onClick={() => makeCall()}
              disabled={!localNumber}
              className={`${!localNumber ? 'bg-gray-700 text-gray-700' : 'bg-green-500 hover:bg-green-600 text-gray-900'
              } rounded-full py-3 px-4 flex items-center justify-center`}
            >
              <Phone color='white' />
            </button>
          ) : (
            <button
              onClick={hangUp}
              className="bg-red-600 hover:bg-red-700 text-gray-900 rounded-full py-3 px-4 flex items-center justify-center"
            >
              <PhoneOff color='white' />
            </button>
          )}
          
          {status === 'In Call' ? (
            <button
              onClick={toggleMute}
              className={`${isMuted ? 'bg-purple-700 border-purple-500' : 'bg-gray-100 border-gray-300'} border rounded-full py-3 px-4 flex items-center justify-center text-gray-700`}
            >
              {isMuted ? (<MicOff color='white' />) : (<Mic color='gray' />)}
            </button>
          ) : (
            <button
              onClick={deleteLastDigit}
              disabled={status === 'In Call'}
              className="bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 border rounded-full py-3 px-4 flex items-center justify-center"
            >
              ⌫
            </button>
          )}
        </div>
      </div>
      {/* The modal for contacts */}
      {showContactsModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex justify-center items-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-sm sm:max-w-md mx-4 shadow-lg relative">
          <div className="mt-4 flex justify-between items-center">
            <h3 className="text-xl text-gray-900 font-semibold mb-4">Your Contacts</h3>
            <button
              onClick={() => setShowContactsModal(false)}
              className="text-sm text-gray-700 hover:text-gray-900"
            >
              Close
            </button>
          </div>
      
          {/* Search Input */}
          <input
            type="text"
            placeholder="Search by name..."
            className="w-full px-3 py-2 mb-3 rounded-md bg-gray-100 text-gray-900 border border-gray-300 focus:outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
      
          {/* List of Contacts */}
          <div className="space-y-3 min-h-[280px] max-h-60 overflow-y-auto">
            {contacts.length === 0 ? (
              <p className="text-gray-700 text-sm">No saved contacts yet.</p>
            ) : (
              filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-center justify-between bg-gray-100 hover:bg-gray-200 p-3 rounded-md"
                >
                <button
                  key={contact.id}
                  className="text-left flex-1"
                  onClick={() => {
                    const country = countries.find(c => c.isoCode === contact.iso_code);
                    setSelectedContact(contact);
                    setSelectedCountry({
                      name: country.name,
                      dialCode: contact.dial_code,
                      isoCode: country.isoCode,
                      emoji: country.emoji,
                    });
                    setLocalNumber(contact.phone_number.replace(contact.dial_code, ''));
                    setIsRiskyNumber(isHighRiskNumber(contact.phone_number));
                    setShowContactsModal(false);
                  }}
                >
                  <div className="text-gray-900 font-medium">{contact.name}</div>
                  <div className="text-gray-700 text-sm">{contact.phone_number}</div>
                </button>
                <button
                  onClick={() => deleteContact(contact.id)}
                  className="ml-3 text-gray-700 hover:text-red-500"
                  title="Delete contact"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      )}
      {/* Save contact prompt */}
      {showSaveModal && (
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex justify-center items-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-sm mx-4 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-900 text-lg font-semibold">Save Contact</h3>
            <button
              onClick={() => setShowSaveModal(false)}
              className="text-sm text-gray-700 hover:text-gray-900"
            >
              Close
            </button>
          </div>
          <label className="text-sm text-gray-700 mb-1 block">Contact Name</label>
          <input
            type="text"
            placeholder="Enter contact name"
            value={newContactName}
            onChange={(e) => setNewContactName(e.target.value)}
            className="w-full px-3 py-2 mb-4 rounded-md bg-gray-100 text-gray-900 border border-gray-300 focus:outline-none"
          />
          <button
            onClick={() => {
              if (newContactName.trim()) {
                const cleanedNumber = localNumber.replace(/^0+/, '');
                const fullNumber = localNumber.startsWith('+')
                  ? localNumber
                  : `${selectedCountry.dialCode}${cleanedNumber}`;

                saveContact({
                  name: newContactName.trim(),
                  phone_number: fullNumber,
                  dial_code: selectedCountry.dialCode,
                  iso_code: selectedCountry.isoCode
                });
                setShowSaveModal(false);
                setNewContactName('');
              }
            }}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md font-semibold transition"
          >
            Save
          </button>
        </div>
      </div>
    )}

    {showVerifyModal && (
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex justify-center items-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-sm mx-4 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-900 text-lg font-semibold">Verify Caller ID</h3>
            <button
              onClick={() => {
                setShowVerifyModal(false);
                setValidationCode(null); // clear old code
              }}
              className="text-sm text-gray-700 hover:text-gray-900"
            >
              Close
            </button>
          </div>

          <p className="text-sm text-gray-700 mb-4">
          Import a phone number you own so your calls appear to come from it. To verify ownership, we will call the number and ask you to confirm.
          </p>

          <label className="text-sm text-gray-700 mb-1 block">Phone Number</label>
          <input
            type="tel"
            placeholder="+1234567890"
            value={importNumber}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^\d+]/g, '');
              setImportNumber(cleaned);
              setValidationCode(null);
              setCallerIdError('');
            }}
            className="w-full px-3 py-2 mb-4 rounded-md bg-gray-100 text-gray-900 border border-gray-300 focus:outline-none"
          />

          {callerIdError && (
            <p className="text-red-500 text-sm mb-3">{callerIdError}</p>
          )}


          <button
          disabled={validationCode}
            onClick={async () => {
              if (!importNumber || !/^\+\d{7,15}$/.test(importNumber)) {
                setCallerIdError('Enter a valid phone number with country code. Example: +447123456789');
                return;
              }
              try {
                const res = await authFetch(
                  `${import.meta.env.VITE_BASE_URL}/api/caller-id/request-verification`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ phoneNumber: importNumber })
                  },
                  { token, login, logout }
                );
                
                if (res.ok) {
                  const json = await res.json();
                  setValidationCode(json.code);
                  addToast('success', 'We will call you shortly. Enter the code when asked.');
                  // Start polling every 10 seconds for up to 90 seconds
                  let attempts = 0;
                  const interval = setInterval(async () => {
                    const verified = await checkVerificationStatus(importNumber);
                    if (verified) {
                      clearInterval(interval);
                      setShowVerifyModal(false);
                      setImportNumber('');
                      setValidationCode(null);
                      
                      addToast('success', 'Number successfully verified!');
                    }

                    attempts++;
                    if (attempts >= 9) {
                      clearInterval(interval);
                    }
                  }, 10000);
                } else {
                  addToast('error', 'Could not start verification.');
                }
              } catch (err) {
                console.error('Verification error:', err);
                addToast('error', res.data.message);
              }
            }}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md font-semibold transition"
          >
            Verify
          </button>
          {/* Validation Code Display */}
          {validationCode && (
            <div className="mt-6 text-center bg-gray-100 border border-purple-500 rounded-lg px-4 py-3">
              <p className="text-sm text-gray-700 mb-1">Your verification code:</p>
              <p className="text-2xl font-bold text-purple-400 tracking-wider">{validationCode}</p>
              <p className="text-xs text-gray-700 mt-2">Enter this code when prompted during the call, then wait for the call to finish.</p>
            </div>
          )}
        </div>
      </div>
      )}

      {showFeedback && (<FeedbackModal onClose={() => setShowFeedback(false)} />)}

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-purple-600 text-gray-700 flex justify-around items-center h-16 z-40 md:hidden">
        <Link to="/call" className="flex flex-col items-center hover:text-purple-400 transition">
          <Phone className="w-5 h-5 mb-0.5" />
          <span className="text-xs">Phone</span>
        </Link>
        <Link to="/call-history" className="flex flex-col items-center hover:text-purple-400 transition">
          <svg className="w-5 h-5 mb-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8v5h4v-2h-2V8z" />
            <path fillRule="evenodd" d="M12 2a10 10 0 100 20 10 10 0 000-20zM4 12a8 8 0 1116 0A8 8 0 014 12z" clipRule="evenodd" />
          </svg>
          <span className="text-xs">History</span>
        </Link>
        <Link to="/profile" className="flex flex-col items-center hover:text-purple-400 transition">
          <svg className="w-5 h-5 mb-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" />
          </svg>
          <span className="text-xs">Profile</span>
        </Link>
      </div>


    </div>
    
  );
};

export default WebPhone;